import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { PublicLocationSidebarDetail } from "./PublicLocationSidebarDetail";

interface PublicLocationSidebarProps {
  token: string;
  locationId: string | null;
  onClose: () => void;
  onZoomToLocation: (locationId: string) => void;
}

function formatPrimaryType(type: string | null): string {
  if (!type) return "Unknown type";
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function PublicLocationSidebar({
  token,
  locationId,
  onClose,
  onZoomToLocation,
}: PublicLocationSidebarProps) {
  const { data: location, isLoading } = trpc.share.getLocation.useQuery(
    { token, locationId: locationId! },
    { enabled: !!locationId },
  );

  if (!locationId) return null;

  return (
    <div
      data-location-sidebar
      className="absolute top-0 right-0 bottom-0 z-20 w-[560px] 2xl:w-[560px] xl:w-[480px] lg:w-[440px] bg-background border-l shadow-xl flex flex-col"
      style={{
        transform: locationId ? "translateX(0)" : "translateX(100%)",
        transition: "transform 300ms ease-in-out",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-4 py-3 shrink-0">
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <>
              <Skeleton className="h-5 w-40 mb-1" />
              <Skeleton className="h-4 w-24" />
            </>
          ) : (
            <>
              <h2 className="text-sm font-semibold truncate">
                {location?.name ?? "Location"}
              </h2>
              <p className="text-xs text-muted-foreground truncate">
                {formatPrimaryType(location?.primary_type ?? null)}
              </p>
            </>
          )}
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="size-8"
          title="Close sidebar"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <PublicLocationSidebarDetail
          token={token}
          locationId={locationId}
          onScreenshotClick={() => onZoomToLocation(locationId)}
        />
      </div>
    </div>
  );
}
